import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useTalentPoolLabels } from '../../contexts/TalentPoolLabelsContext';
import { adminAlertStyle } from '../../styles/adminPageShell';
import { PoolRelationWeightsModal } from './PoolRelationWeightsModal';

interface TalentPoolRow {
  id: number;
  code: string;
  displayName: string;
  enabled: boolean;
  asEvaluatee: boolean;
  isSystem: boolean;
  sortOrder: number;
  /** 评价关系分组（如 医生 / 助理 / 客服 / 美容 / 总部） */
  relationGroup: string | null;
  /** 评价关系职级：数字越大层级越高（基层 1 / 中层 2 / 门店高层 3 / 总部高层 4） */
  relationLevel: number | null;
}

export function TalentPoolDictSection() {
  const qc = useQueryClient();
  const { getRelationGroupColor } = useTalentPoolLabels();
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<TalentPoolRow | null>(null);
  const [weightsPool, setWeightsPool] = useState<TalentPoolRow | null>(null);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const { data: pools = [], isFetching } = useQuery<TalentPoolRow[]>({
    queryKey: ['talent-pools'],
    queryFn: () => api.get('/admin/talent-pools') as Promise<TalentPoolRow[]>,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['talent-pools'] });
  };
  const onErr = (e: any) => message.error(e?.response?.data?.message || e?.message || '操作失败');

  const createMut = useMutation({
    mutationFn: (v: {
      code: string;
      displayName: string;
      asEvaluatee?: boolean;
      relationGroup?: string;
      relationLevel?: number;
    }) => api.post('/admin/talent-pools', v),
    onSuccess: () => {
      message.success('已新增人才池');
      setAddOpen(false);
      addForm.resetFields();
      invalidate();
    },
    onError: onErr,
  });

  const updateMut = useMutation({
    mutationFn: (v: {
      id: number;
      displayName?: string;
      enabled?: boolean;
      asEvaluatee?: boolean;
      relationGroup?: string | null;
      relationLevel?: number | null;
    }) =>
      api.patch(`/admin/talent-pools/${v.id}`, {
        displayName: v.displayName,
        enabled: v.enabled,
        asEvaluatee: v.asEvaluatee,
        relationGroup: v.relationGroup,
        relationLevel: v.relationLevel,
      }),
    onSuccess: () => {
      message.success('已保存');
      setEditing(null);
      editForm.resetFields();
      invalidate();
    },
    onError: onErr,
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/talent-pools/${id}`),
    onSuccess: () => {
      message.success('已删除');
      invalidate();
    },
    onError: onErr,
  });

  const openEdit = (r: TalentPoolRow) => {
    setEditing(r);
    editForm.setFieldsValue({
      displayName: r.displayName,
      asEvaluatee: r.asEvaluatee !== false,
      relationGroup: r.relationGroup ?? '',
      relationLevel: r.relationLevel ?? undefined,
    });
  };

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={adminAlertStyle}
        message="人才池字典"
        description={
          <ul style={{ margin: '4px 0 0 18px', padding: 0, fontSize: 12 }}>
            <li>新租户默认为空，请自行「新增人才池」；系统不会预置任何池。</li>
            <li>可改名、停用、删除；code 由 HR 手填（A-Z 0-9 _）。已被员工或维度模板引用的池需先迁移再删。</li>
            <li>
              「作为被评价人」关闭 = 不盘：花名册仍可用该池、领导推断仍生效、本人可评别人；但不会作为被评价人、不种素质模板。
            </li>
            <li>
              「分组 + 职级」用于自动推断评价关系：同分组内职级高的是领导；同门店同职级互为同级同事；
              员工缺「直属上级」时按 同分组 + 同门店 + 最近更高职级 唯一匹配领导（多人歧义交 HR 处理）。
            </li>
            <li>
              点击「显示名」弹出本页表格，可对该池评价角色做新增 / 改权重 / 删除（作用在所选盘点批次模板）。
            </li>
          </ul>
        }
      />
      <Card
        style={{ marginTop: 16 }}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
            新增人才池
          </Button>
        }
      >
        <Table<TalentPoolRow>
          rowKey="id"
          loading={isFetching}
          dataSource={pools}
          pagination={false}
          size="middle"
          locale={{ emptyText: '暂无人才池，请点击右上角「新增人才池」' }}
          columns={[
            {
              title: 'Code',
              dataIndex: 'code',
              width: 160,
              render: (v: string) => <Tag color="blue">{v}</Tag>,
            },
            {
              title: '显示名',
              dataIndex: 'displayName',
              render: (v: string, r) => (
                <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => setWeightsPool(r)}>
                  {v}
                </Button>
              ),
            },
            {
              title: '分组',
              dataIndex: 'relationGroup',
              width: 100,
              render: (v: string | null) =>
                v ? <Tag color={getRelationGroupColor(v)}>{v}</Tag> : <span style={{ color: '#bbb' }}>未配置</span>,
            },
            {
              title: '职级',
              dataIndex: 'relationLevel',
              width: 70,
              render: (v: number | null) =>
                v != null ? <Tag color="geekblue">{v}</Tag> : <span style={{ color: '#bbb' }}>—</span>,
            },
            {
              title: '启用',
              dataIndex: 'enabled',
              width: 80,
              render: (v: boolean, r) => (
                <Switch
                  checked={v}
                  loading={updateMut.isPending}
                  onChange={(checked) => updateMut.mutate({ id: r.id, enabled: checked })}
                />
              ),
            },
            {
              title: '作为被评价人',
              dataIndex: 'asEvaluatee',
              width: 120,
              render: (v: boolean, r) => (
                <Switch
                  checked={v !== false}
                  checkedChildren="是"
                  unCheckedChildren="不盘"
                  loading={updateMut.isPending}
                  onChange={(checked) => updateMut.mutate({ id: r.id, asEvaluatee: checked })}
                />
              ),
            },
            {
              title: '操作',
              key: 'op',
              width: 160,
              render: (_v, r) => (
                <Space>
                  <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
                    编辑
                  </Button>
                  <Popconfirm
                    title={`删除人才池「${r.displayName}」？`}
                    description="未被员工或维度模板使用的池才可删除。"
                    onConfirm={() => removeMut.mutate(r.id)}
                  >
                    <Button size="small" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title="新增人才池"
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={() => addForm.submit()}
        confirmLoading={createMut.isPending}
        destroyOnClose
      >
        <Form
          form={addForm}
          layout="vertical"
          initialValues={{ asEvaluatee: true }}
          onFinish={(v) =>
            createMut.mutate({
              code: v.code,
              displayName: (v.displayName || '').trim(),
              asEvaluatee: v.asEvaluatee !== false,
              relationGroup: (v.relationGroup || '').trim() || undefined,
              relationLevel: v.relationLevel ?? undefined,
            })
          }
        >
          <Form.Item
            label="Code（A-Z 0-9 _，租户内唯一）"
            name="code"
            rules={[
              { required: true, message: 'code 必填' },
              { pattern: /^[A-Za-z0-9_]+$/, message: '只能包含字母、数字、下划线' },
            ]}
          >
            <Input placeholder="如 STORE_OPS" maxLength={32} />
          </Form.Item>
          <Form.Item
            label="显示名"
            name="displayName"
            rules={[{ required: true, message: '显示名必填' }]}
          >
            <Input placeholder="如 门店运营池" maxLength={50} />
          </Form.Item>
          <Form.Item
            label="作为被评价人"
            name="asEvaluatee"
            valuePropName="checked"
            extra="关闭后该池员工不盘（不当被评价人），但仍可评价他人、参与领导推断。"
          >
            <Switch checkedChildren="是" unCheckedChildren="不盘" />
          </Form.Item>
          <Form.Item
            label="评价关系分组（同分组内按职级认领导，可选）"
            name="relationGroup"
          >
            <Input placeholder="如 医生 / 助理 / 客服 / 美容 / 总部" maxLength={32} />
          </Form.Item>
          <Form.Item
            label="评价关系职级（数字越大层级越高，可选）"
            name="relationLevel"
          >
            <InputNumber min={1} max={99} placeholder="如 基层1 / 中层2 / 门店高层3 / 总部高层4" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`编辑 · ${editing?.code ?? ''}`}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={() => editForm.submit()}
        confirmLoading={updateMut.isPending}
        destroyOnClose
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(v) =>
            editing &&
            updateMut.mutate({
              id: editing.id,
              displayName: (v.displayName || '').trim(),
              asEvaluatee: v.asEvaluatee !== false,
              relationGroup: (v.relationGroup || '').trim() || null,
              relationLevel: v.relationLevel ?? null,
            })
          }
        >
          <Form.Item label="Code">
            <Input value={editing?.code} disabled />
          </Form.Item>
          <Form.Item
            label="显示名"
            name="displayName"
            rules={[{ required: true, message: '显示名必填' }]}
          >
            <Input maxLength={50} placeholder="如 高层管理池" />
          </Form.Item>
          <Form.Item
            label="作为被评价人"
            name="asEvaluatee"
            valuePropName="checked"
            extra="关闭 = 不盘：不当被评价人；花名册、领导推断、本人评别人均不受影响。"
          >
            <Switch checkedChildren="是" unCheckedChildren="不盘" />
          </Form.Item>
          <Form.Item label="评价关系分组（同分组内按职级认领导；留空 = 清除）" name="relationGroup">
            <Input maxLength={32} placeholder="如 医生 / 助理 / 客服 / 美容 / 总部" />
          </Form.Item>
          <Form.Item label="评价关系职级（数字越大层级越高；留空 = 清除）" name="relationLevel">
            <InputNumber min={1} max={99} placeholder="如 基层1 / 中层2 / 门店高层3 / 总部高层4" style={{ width: '100%' }} />
          </Form.Item>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            修改后刷新维度库、评价关系等页面即可生效；分组/职级变化后请到「评价关系 → 按组织关系生成/同步」重新预览。
          </Typography.Text>
        </Form>
      </Modal>

      <PoolRelationWeightsModal
        open={!!weightsPool}
        pool={weightsPool ? { code: weightsPool.code, displayName: weightsPool.displayName } : null}
        onClose={() => setWeightsPool(null)}
      />
    </>
  );
}
